<template>
    <Toast />
    <Spinner v-if="isLoading" />
    <div class="grid nested-grid">
        <div class="col-12">
            <div class="card fluid cardheader">
                <div class="col-12">
                    <h5 class="stationsHeader">{{ translated('newDocumentGenerationTitle') }}</h5>
                </div>
            </div>
        </div>

        <div class="col-12">
            <div class="card fluid">
                <div class="field grid">
                    <label class="col-12 md:col-3 mb-md-0">{{ translated('documentGenerationType') }}</label>
                    <div class="col-12 md:col-9">
                        <Dropdown v-model="form.publicprivate" :options="publicPrivateOptions" optionLabel="label" optionValue="value" @change="filtersChanged" />
                    </div>
                </div>

                <div class="field grid">
                    <label class="col-12 md:col-3 mb-md-0">{{ translated('documentGenerationSource') }}</label>
                    <div class="col-12 md:col-9">
                        <Dropdown v-model="selectedSource" :options="sourceOptions" optionLabel="label" optionValue="value" @change="sourceChanged" />
                    </div>
                </div>

                <div class="field grid" v-if="selectedSource === 'snapshot'">
                    <label class="col-12 md:col-3 mb-md-0">{{ translated('snapshotcombolabel') }}</label>
                    <div class="col-12 md:col-9">
                        <Dropdown v-model="form.snapshotId" :options="snapshotOptions" optionLabel="label" optionValue="value" :placeholder="translated('snapshotcombotip')" @change="snapshotChanged" />
                    </div>
                </div>

                <div class="field grid">
                    <label class="col-12 md:col-3 mb-md-0">{{ translated('financiranjerefillpointlabel') }}</label>
                    <div class="col-12 md:col-9">
                        <Dropdown v-model="form.financedid" :options="financedOptions" optionLabel="label" optionValue="value" :placeholder="translated('documentGenerationFinancedAll')" :showClear="true" @change="filtersChanged" />
                    </div>
                </div>

                <div class="field grid">
                    <label class="col-12 md:col-3 mb-md-0">{{ translated('documentGenerationFilterCpoName') }}</label>
                    <div class="col-12 md:col-9">
                        <InputText type="text" v-model="form.cponame" @input="filtersChanged" />
                    </div>
                </div>

                <div class="field grid">
                    <label class="col-12 md:col-3 mb-md-0">{{ translated('documentGenerationFilterEvseId') }}</label>
                    <div class="col-12 md:col-9">
                        <InputText type="text" v-model="form.evseid" @input="filtersChanged" />
                    </div>
                </div>

                <div class="field grid">
                    <label class="col-12 md:col-3 mb-md-0">{{ translated('documentGenerationFilterUpravicenec') }}</label>
                    <div class="col-12 md:col-9">
                        <InputText type="text" v-model="form.upravicenec" @input="filtersChanged" />
                    </div>
                </div>

                <div class="page-actions">
                    <Button :label="translated('documentGenerationBackToOverview')" icon="pi pi-arrow-left" class="p-button-text" @click="goBack" />
                    <Button :label="translated('documentGenerationPreview')" icon="pi pi-search" class="p-button-secondary" @click="loadPreview" />
                    <Button :label="translated('documentGenerationGenerate')" icon="pi pi-play" @click="generate" :disabled="!previewLoaded" />
                </div>
            </div>
        </div>

        <div class="col-12" v-if="previewLoaded">
            <div class="card fluid">
                <div class="preview-summary">
                    <div><strong>{{ translated('documentGenerationType') }}:</strong> {{ form.publicprivate ? translated('documentGenerationPublic') : translated('documentGenerationPrivate') }}</div>
                    <div><strong>{{ translated('documentGenerationDocuments') }}:</strong> {{ preview.DocumentCount }}</div>
                </div>

                <div v-for="document in preview.Documents" :key="document.FileNamePreview" class="preview-document">
                    <h6>{{ document.FileNamePreview }}</h6>
                    <div class="preview-company">
                        <div><strong>{{ translated('companyname') }}:</strong> {{ document.CompanyName }}</div>
                        <div v-if="document.CompanyTaxId"><strong>{{ translated('taxid') }}:</strong> {{ document.CompanyTaxId }}</div>
                    </div>

                    <DataTable :value="document.Items" class="p-datatable-sm" showGridlines responsiveLayout="scroll">
                        <Column :header="translated('documentGenerationFilterEvseId')" field="EvseId"></Column>
                        <Column :header="translated('energysite')" field="SiteName"></Column>
                        <Column :header="translated('documentGenerationAddress')">
                            <template #body="slotProps">
                                <div class="multiline-cell">{{ buildAddress(slotProps.data) }}</div>
                            </template>
                        </Column>
                        <Column :header="translated('documentGenerationFilterFinanced')">
                            <template #body="slotProps">
                                <div class="multiline-cell">{{ buildFinancedText(slotProps.data) }}</div>
                            </template>
                        </Column>
                        <Column :header="translated('documentGenerationReceiving')">
                            <template #body="slotProps">
                                <div class="multiline-cell">{{ buildReceivingText(slotProps.data) }}</div>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import createHttp from "@/services/http";
import { computed } from "vue";
import Spinner from "@/components/Spinner.vue";
import store from "@/store";

export default {
    name: 'NewDocumentGeneration',
    components: { Spinner },
    data() {
        return {
            translated: computed(() => store.getters.getTranslate),
            isLoading: false,
            previewLoaded: false,
            previewSignature: '',
            preview: {
                DocumentCount: 0,
                Documents: []
            },
            selectedSource: 'live',
            snapshotOptions: [],
            financedOptions: [],
            form: {
                publicprivate: true,
                snapshotId: null,
                financedid: null,
                cponame: '',
                evseid: '',
                upravicenec: ''
            }
        };
    },
    computed: {
        sourceOptions() {
            return [
                { label: this.translated('documentGenerationSourceLive'), value: 'live' },
                { label: this.translated('documentGenerationSourceSnapshot'), value: 'snapshot' }
            ];
        },
        publicPrivateOptions() {
            return [
                { label: this.translated('documentGenerationPublic'), value: true },
                { label: this.translated('documentGenerationPrivate'), value: false }
            ];
        }
    },
    async mounted() {
        await this.loadInitialData();
    },
    methods: {
        filtersChanged() {
            this.invalidatePreview();
        },
        invalidatePreview() {
            this.previewLoaded = false;
            this.previewSignature = '';
            this.preview = {
                DocumentCount: 0,
                Documents: []
            };
        },
        async loadInitialData() {
            this.isLoading = true;
            try {
                const http = createHttp(true);
                const [snapshotsResult, financedResult] = await Promise.all([
                    http.get(process.env.VUE_APP_API_FOLDER + "/GetSnapshotRecords"),
                    http.get(process.env.VUE_APP_API_FOLDER + "/GetFinanceRecords")
                ]);

                this.snapshotOptions = (snapshotsResult.data || []).map(item => ({
                    label: this.formatDate(item.SnapshotDate),
                    value: item.Id
                }));

                this.setFinancedOptions(financedResult.data || []);
            }
            finally {
                this.isLoading = false;
            }
        },
        async sourceChanged() {
            this.invalidatePreview();
            this.form.financedid = null;

            if (this.selectedSource === 'live') {
                this.form.snapshotId = null;
                await this.loadLiveFinanced();
                return;
            }

            this.financedOptions = [];
        },
        async snapshotChanged() {
            this.invalidatePreview();
            this.form.financedid = null;

            if (!this.form.snapshotId) {
                this.financedOptions = [];
                return;
            }

            await this.loadSnapshotFinanced(this.form.snapshotId);
        },
        async loadLiveFinanced() {
            const http = createHttp(true);
            const result = await http.get(process.env.VUE_APP_API_FOLDER + "/GetFinanceRecords");
            this.setFinancedOptions(result.data || []);
        },
        async loadSnapshotFinanced(snapshotId) {
            const http = createHttp(true);
            const result = await http.get(process.env.VUE_APP_API_FOLDER + "/GetSnapshotFinancedRecords", {
                params: { snapshotId }
            });
            this.setFinancedOptions(result.data || []);
        },
        setFinancedOptions(records) {
            this.financedOptions = (records || []).map(item => ({
                label: item.Name,
                value: item.Id
            }));
        },
        buildPreviewParams() {
            return {
                publicprivate: this.form.publicprivate,
                cponame: this.form.cponame ?? '',
                evseid: this.form.evseid ?? '',
                upravicenec: this.form.upravicenec ?? '',
                financedid: this.form.financedid,
                snapshotId: this.selectedSource === 'snapshot' ? this.form.snapshotId : null
            };
        },
        buildPreviewSignature() {
            return JSON.stringify(this.buildPreviewParams());
        },
        async loadPreview() {
            if (this.selectedSource === 'snapshot' && !this.form.snapshotId) {
                this.$toast.add({ severity: 'error', summary: 'Error', detail: this.translated('documentGenerationSelectSnapshotError'), life: 4000 });
                return;
            }

            this.isLoading = true;
            try {
                const http = createHttp(true);
                const result = await http.get(process.env.VUE_APP_API_FOLDER + "/GetRefillPointDocumentDataSource", {
                    params: this.buildPreviewParams()
                });

                this.preview = result.data;
                this.previewLoaded = true;
                this.previewSignature = this.buildPreviewSignature();
            }
            catch (error) {
                this.invalidatePreview();

                const detail = error?.response?.data?.message || this.translated('documentGenerationPreviewError');
                this.$toast.add({ severity: 'error', summary: 'Error', detail, life: 5000 });
            }
            finally {
                this.isLoading = false;
            }
        },
        async generate() {
            if (!this.previewLoaded) {
                return;
            }

            if (this.previewSignature !== this.buildPreviewSignature()) {
                this.invalidatePreview();
                this.$toast.add({ severity: 'error', summary: 'Error', detail: this.translated('documentGenerationFiltersChangedError'), life: 5000 });
                return;
            }

            this.isLoading = true;
            try {
                const http = createHttp(true);
                await http.post(process.env.VUE_APP_API_FOLDER + "/QueueRefillPointDocuments", this.buildPreviewParams());
                this.$router.push({ name: 'DocumentGeneration' });
            }
            catch (error) {
                const detail = error?.response?.data?.message || this.translated('documentGenerationQueueError');
                this.$toast.add({ severity: 'error', summary: 'Error', detail, life: 5000 });
            }
            finally {
                this.isLoading = false;
            }
        },
        goBack() {
            this.$router.push({ name: 'DocumentGeneration' });
        },
        buildAddress(item) {
            return [item.SiteAddress, item.SiteZip, item.SiteCity].filter(x => x && String(x).trim() !== '').join('\n');
        },
        buildFinancedText(item) {
            const lines = [];
            if (item.FinancedName) lines.push(item.FinancedName);
            if (item.FinancedSourceName) lines.push(item.FinancedSourceName);
            if (item.FinancedSourceInstrument) lines.push(item.FinancedSourceInstrument);
            return lines.join('\n');
        },
        buildReceivingText(item) {
            const lines = [];
            if (item.ReceivingSupportCompanyName) lines.push(item.ReceivingSupportCompanyName);
            if (item.ReceivingSupportCompanyVATId) lines.push(item.ReceivingSupportCompanyVATId);
            if (item.ReceivingAgreementName) lines.push(item.ReceivingAgreementName);
            return lines.join('\n');
        },
        formatDate(dateTime) {
            if (!dateTime) {
                return '';
            }

            const d = new Date(dateTime);
            return d.toLocaleString('sl-SI', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        }
    }
}
</script>

<style scoped>
.page-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1.5rem;
    flex-wrap: wrap;
}

.preview-summary {
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
}

.preview-document + .preview-document {
    margin-top: 1.5rem;
}

.preview-company {
    margin-bottom: 0.75rem;
}

.multiline-cell {
    white-space: pre-line;
    word-break: break-word;
}
</style>
